import { GetMessagesPage as DLGetMessagesPage, GetAllMessages as DLGetAllMessages, GetNewerMessages as DLGetNewerMessages, SendMessage as DLSendMessage, ToggleMessageVisibility as DLToggleVisibility, ToggleMessageShown as DLToggleShown, SetMessageDesign as DLSetDesign, DeleteMessage as DLDelete, SelectMessage as DLSelect, DeselectMessage as DLDeselect, ResetMessages as DLReset, SetOverlayPosition as DLSetOverlayPosition } from './../../../DL/Admin/Overlays/Messages'


/**
 * ============================================================================
 * BUSINESS LOGIC: GESTIÓN DE MENSAJES
 * ============================================================================
 * Encargado de la validación de datos, normalización de respuestas y reglas de negocio.
 * Actúa como puente entre la UI y el Data Layer.
 */

// Función auxiliar para sanitizar strings (Prevención básica XSS/SQLi)
// Nota: El Backend también debe sanitizar, esto es una capa extra.
const sanitizeInput = (str) => {
    if (!str) return "";
    return str.replace(/<[^>]*>?/gm, "").trim(); 
};

// /**
//  * Obtiene una página de mensajes con validación de parámetros.
//  * Devuelve un objeto limpio con items y cursor.
//  */
// const GetMessagesPage = async ({ limit = 30, cursor = null, sort = 'DESC' } = {}) => {
//     try {
//         // Validaciones / Normalización
//         const safeLimit = Math.max(1, Math.min(Number(limit), 100)); // Min 1, Max 100
//         const safeSort = sort === 'ASC' ? 'ASC' : 'DESC';

//         const response = await DLGetMessagesPage({ limit: safeLimit, cursor, sort: safeSort });

//         if (response.success) {
//             // Aseguramos que la estructura de datos sea consistente para la UI
//             // El backend devuelve: { items: [...], nextCursor: ... }
//             const safeItems = (response.data.items || []).map(m => ({
//                 ...m,
//                 message: m.message || "", // Fallback
//                 sender: m.sender || "Anónimo"
//             }));

//             return { 
//                 success: true, 
//                 data: { 
//                     items: safeItems, 
//                     nextCursor: response.data.nextCursor 
//                 } 
//             };
//         } else {
//             return { success: false, message: response.message };
//         }
//     } catch (error) {
//         return { success: false, message: 'Error de lógica al obtener mensajes.' };
//     }
// };

/**
 * Obtiene una página de mensajes con validación de parámetros.
 * Devuelve un objeto limpio con items y cursor.
 */
const GetMessagesPage = async ({ limit = 30, cursor = null, sort = 'DESC', onlyWithPhone = false } = {}) => {
    try {
        // Validaciones / Normalización
        const safeLimit = Math.max(1, Math.min(Number(limit), 100));
        const safeSort = sort === 'ASC' ? 'ASC' : 'DESC';

        // Pasa 'onlyWithPhone' al Data Layer
        const response = await DLGetMessagesPage({ 
            limit: safeLimit, 
            cursor, 
            sort: safeSort, 
            onlyWithPhone 
        });

        if (response.success) {
            const safeItems = (response.data.items || []).map(m => ({
                ...m,
                message: m.message || "", 
                sender: m.sender || "Anónimo",
                phone: m.phone || null // Aseguramos que el campo exista en el objeto final
            }));

            return { 
                success: true, 
                data: { 
                    items: safeItems, 
                    nextCursor: response.data.nextCursor 
                } 
            };
        } else {
            return { success: false, message: response.message };
        }
    } catch (error) {
        return { success: false, message: 'Error de lógica al obtener mensajes.' };
    }
};

/**
 * Obtiene todos los mensajes sin paginación (para exportación).
 */
const GetAllMessages = async ({ onlyWithPhone = false } = {}) => {
    try {
        const response = await DLGetAllMessages({ onlyWithPhone });

        if (response.success) {
            const safeItems = (response.data || []).map(m => ({
                ...m,
                message: m.message || "", 
                sender: m.sender || "Anónimo",
                phone: m.phone || null
            }));

            return { 
                success: true, 
                data: safeItems
            };
        } else {
            return { success: false, message: response.message };
        }
    } catch (error) {
        return { success: false, message: 'Error de lógica al obtener todos los mensajes.' };
    }
};

/**
 * Busca mensajes nuevos posteriores a un ID dado.
 */
const GetNewerMessages = async (lastId) => {
    try {
        if (typeof lastId !== 'number' || lastId < 0) return { success: true, data: [] };

        const response = await DLGetNewerMessages(lastId);

        if (response.success) {
            return { success: true, data: response.data || [] };
        } else {
            return { success: false, message: response.message };
        }
    } catch (error) {
        return { success: false, message: 'Error al procesar nuevos mensajes.' };
    }
};

/**
 * Envía un mensaje con validaciones de contenido.
 */
const SendMessage = async (messageData) => {
    try {
        // 1. Validaciones
        if (!messageData || !messageData.message || messageData.message.trim() === '') {
            return { success: false, message: 'El mensaje no puede estar vacío.' };
        }

        // 2. Sanitización
        const cleanMessage = sanitizeInput(messageData.message);
        const cleanSender = sanitizeInput(messageData.sender);

        if (cleanMessage.length > 100) {
            return { success: false, message: 'El mensaje es demasiado largo (máx 100 caracteres).' };
        }

        // 3. Enviar al DL
        const response = await DLSendMessage({ message: cleanMessage, sender: cleanSender });

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, message: response.message };
        }
    } catch (error) {
        return { success: false, message: 'Error interno al enviar mensaje.' };
    }
};

// --- FUNCIONES PASS-THROUGH (Simples pases al DL con manejo de error estándar) ---

const ToggleMessageHide = async (id) => {
    try {
        return await DLToggleVisibility(id);
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const ToggleMessageShown = async (id) => {
    try {
        return await DLToggleShown(id);
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const SetMessageDesign = async (id, designId) => {
    try {
        return await DLSetDesign(id, designId);
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const DeleteMessage = async (id) => {
    try {
        return await DLDelete(id);
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const SelectMessage = async (id) => {
    try {
        return await DLSelect(id);
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const DeselectMessage = async () => {
    try {
        return await DLDeselect();
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const ResetMessages = async () => {
    try {
        return await DLReset();
    }
    catch {
        return { success: false, message: 'Error BL.' };
    }
};

const SetOverlayPosition = async (offset) => {
    try {
        return await DLSetOverlayPosition(offset)
    } catch {
        return { success: false, message: '' };
    }
};

export { GetMessagesPage, GetAllMessages, GetNewerMessages, SendMessage, ToggleMessageHide, ToggleMessageShown, SetMessageDesign, DeleteMessage, SelectMessage, DeselectMessage, ResetMessages, SetOverlayPosition };









// import { GetMessages as DLGetMessages, SendMessage as DLSendMessage } from './../../../DL/Admin/Overlays/Messages'


// const GetMessages = async (quantity = 10) => {
//     try {
//         debugger
//         const response = await DLGetMessages(quantity)
//         debugger
//         if (response.success) {
//             return { success: true, data: response.data }
//         } else {
//             return { success: false, message: response.message }
//         }
//     } catch (error) {
//         return { success: false, message: 'No ha sido posible obtener el listado de mensajes' }
//     }
// }

// const SendMessage = async (message) => {
//     try {
//         if (!message.message || message.message === '') {
//             return { success: false, message: '¡Ups! Has olvidado escribir el mensaje. Escribe tu mensaje' }
//         }

//         // Agregar revisión de inyección SQL
//         debugger
//         const response = await DLSendMessage(message)
//         debugger
//         if (response.success) {
//             return { success: true, data: response.data }
//         } else {
//             return { success: false, message: response.message }
//         }
//     } catch (error) {
//         return { success: false, message: 'No ha sido posible enviar tu mensaje. Inténtalo nuevamente o inténtalo más tarde' }
//     }
// }

// export { GetMessages, SendMessage };





// const messages = [
//     {
//         id: 1,
//         message: 'Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje a. Esto es mi mensaje a',
//         sender: 'Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez',
//         design: 0
//     },
//     {
//         id: 2,
//         message: 'Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje a. Esto es mi mensaje a',
//         sender: 'Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez',
//         design: 0
//     },
//     {
//         id: 3,
//         message: 'Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje a. Esto es mi mensaje a',
//         sender: 'Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez',
//         design: 0
//     },
//     {
//         id: 4,
//         message: 'Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje. Esto es mi mensaje a. Esto es mi mensaje a',
//         sender: 'Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez Juanito Pérez',
//         design: 0
//     }
// ]